import { resolveModelId } from '@shared-models';

import type { FallbackProviderEntry, LLMProvider } from '../types/npc';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const isLlmProvider = (value: unknown): value is LLMProvider =>
  value === 'gemini' || value === 'openrouter' || value === 'anthropic';

export const canonicalizeProviderModel = (provider: LLMProvider, model: string): string => {
  const trimmed = model.trim();
  return trimmed ? resolveModelId(trimmed, provider) : '';
};

export const normalizeFallbackProviders = (value: unknown): FallbackProviderEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!isRecord(entry) || !isLlmProvider(entry.llmProvider)) {
      return [];
    }

    const llmModel = canonicalizeProviderModel(
      entry.llmProvider,
      String(entry.llmModel ?? '').trim(),
    );
    if (!llmModel) {
      return [];
    }

    return [
      {
        llmProvider: entry.llmProvider,
        llmModel,
      },
    ];
  });
};

export const cloneFallbackProviders = (
  value: readonly FallbackProviderEntry[] | null | undefined,
): FallbackProviderEntry[] =>
  normalizeFallbackProviders(
    (value ?? []).map((entry) => ({
      llmProvider: entry.llmProvider,
      llmModel: entry.llmModel,
    })),
  );

export const sameFallbackProviders = (
  left: readonly FallbackProviderEntry[] | null | undefined,
  right: readonly FallbackProviderEntry[] | null | undefined,
): boolean => {
  const normalizedLeft = cloneFallbackProviders(left);
  const normalizedRight = cloneFallbackProviders(right);
  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((entry, index) => {
    const other = normalizedRight[index];
    return (
      other?.llmProvider === entry.llmProvider &&
      other.llmModel.toLowerCase() === entry.llmModel.toLowerCase()
    );
  });
};
